import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';

import { logout as signOut, sessionQueries, type SessionUser } from '@/api';

export interface SessionState {
	user: SessionUser | null;
	isAuthenticated: boolean;
	/**
	 * Перший запит сесії ще не завершився. Важливо відрізняти від «гість»:
	 * поки невідомо, хедер не має блимати кнопками Log in / Log out.
	 */
	isPending: boolean;
}

/**
 * Поточна сесія. Джерело — сервер, а не локальний стан: кука httpOnly,
 * тож JS не може дізнатись про вхід інакше, ніж спитавши бекенд.
 */
export function useSession(): SessionState {
	const { data, isPending } = useQuery(sessionQueries.current());

	return {
		user: data?.user ?? null,
		isAuthenticated: data != null,
		isPending,
	};
}

/**
 * Вихід. Після нього кеш скидається повністю, а не правиться руками:
 * єдиним джерелом правди лишається відповідь сервера.
 */
export function useLogout(): { logout: () => void; isPending: boolean } {
	const queryClient = useQueryClient();
	const router = useRouter();

	const mutation = useMutation({
		mutationFn: signOut,
		// Навіть якщо запит не дійшов, локальні дані користувача треба забути:
		// лишити їх на екрані після натискання Log out — гірше за зайвий запит.
		onSettled: async () => {
			// Сесію ПЕРЕПИТУЄМО в сервера (reset = забути й спитати знову), а не
			// правимо прапорець руками: джерело правди лишається одне.
			// Просто invalidate тут замало — `beforeLoad` читає сесію через
			// `ensureQueryData`, а той віддає кеш навіть протухлий, і маршрут
			// побачив би старого користувача.
			await queryClient.resetQueries({ queryKey: sessionQueries.all() });
			// Решта кешу — дані попереднього дилера: ставки, інвойси, авто.
			// З них бічна панель малює лічильники, тож на спільній машині вони
			// світились би наступному. Видаляємо все, крім самої сесії.
			queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== sessionQueries.all()[0] });
			// Куди вести — вирішує guard маршруту: приватна сторінка перекине
			// на Log in, каталог і Watchlist лишаться відкритими гостю.
			// Список приватних шляхів тут не дублюємо — він розійдеться з маршрутами.
			await router.invalidate();
		},
	});

	return { logout: () => mutation.mutate(), isPending: mutation.isPending };
}
